import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { CommonHttpService } from '../common-http.service';
import { environment } from '../../../environments/environment';
import { IItemDetails } from '../../models/IItemDetails';
import { firstValueFrom } from 'rxjs';

class CachedElement<T> {
  value: T;
  expirationDate: number;
  msTimeToLive: number;

  constructor(value: T, expirationDate: number, msTimeToLive: number) {
    this.value = value;
    this.expirationDate = expirationDate;
    this.msTimeToLive = msTimeToLive;
  }

  public isExpired(): boolean {
    return this.expirationDate + this.msTimeToLive < new Date().getTime();
  }
}

const TIME_TO_LIVE = 1000 * 60 * 2 // 2 minutes;

@Injectable({
  providedIn: 'root'
})
export class ItemDetailsService {
  private oidcSecurityService = inject(OidcSecurityService);
  private httpService = inject(CommonHttpService);

  private itemDetailsCache: Map<number, CachedElement<IItemDetails>> = new Map<number, CachedElement<IItemDetails>>();

  public async getItemDetails(itemId: number): Promise<IItemDetails> {
    if (this.itemDetailsCache.has(itemId) && !this.itemDetailsCache.get(itemId)!.isExpired()) {
      return this.itemDetailsCache.get(itemId)!.value;
    } else {
      const itemDetails = await this.getItemById(itemId);
      this.itemDetailsCache.set(itemId, new CachedElement(itemDetails, new Date().getTime(), TIME_TO_LIVE));
      return itemDetails;
    }
  }

  private async getItemById(id: number): Promise<IItemDetails> {
    const url = `${environment.apiUrl}/v3/items/${id}`;

    let headers = new HttpHeaders();
    const authResult = await firstValueFrom(this.oidcSecurityService.checkAuth());
    if (authResult.isAuthenticated) {
      headers = headers.set('Authorization', `Bearer ${authResult.accessToken}`);
    }
    const response = firstValueFrom(this.httpService.get<IItemDetails>(url, undefined, headers));
    return response;
  }
}
